import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import Konva from 'konva';
import { KonvaEventObject } from 'konva/lib/Node';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { PlanMarker, SymbolShapeConfig, RoomIndicator } from './models/plan-marker.interface';
import { PlanConfig, PlanState } from './models/plan-config.interface';
import { IndicatorDetailDialogComponent } from './indicator-detail-dialog/indicator-detail-dialog.component';
import { ObjektStrukturService } from '../../services/objekt-struktur.service';
import { PlanService } from '../../services/plan.service';

@Component({
  selector: 'app-interactive-plan',
  templateUrl: './interactive-plan.component.html',
  styleUrls: ['./interactive-plan.component.scss']
})
export class InteractivePlanComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @Input() planImageUrl: string = '';
  @Input() roomIndicators: RoomIndicator[] = [];
  @Input() width: number = 600;
  @Input() height: number = 400;
  @Input() zoomLevel: number = 100;
  @Input() planId?: string;
  @Input() existingPositions?: Array<{
    room_indikator_id: string;
    x_position: number;
    y_position: number;
  }>;
  @Input() savePositionCallback?: (marker: PlanMarker, position: { x: number, y: number }) => Observable<any>;
  @Input() planUploadId?: number;
  @Input() planOriginalFilename?: string;
  @Input() planFilePath?: string;
  @Input() planMimeType?: string;
  @Input() readOnly: boolean = true; // Default to read-only for monitoring app
  
  @Output() markerPlaced = new EventEmitter<PlanMarker>();
  @Output() markerMoved = new EventEmitter<PlanMarker>();
  @Output() markerRemoved = new EventEmitter<string>();
  @Output() zoomChanged = new EventEmitter<number>();
  @Output() planExported = new EventEmitter<string>();
  @Output() planSaved = new EventEmitter<void>();

  @ViewChild('konvaContainer', { static: false }) konvaContainer!: ElementRef;
  @ViewChild('interactivePlanContainer', { static: false }) interactivePlanContainer!: ElementRef;

  // Konva objects
  stage: Konva.Stage | null = null;
  backgroundLayer: Konva.Layer | null = null;
  markersLayer: Konva.Layer | null = null;
  uiLayer: Konva.Layer | null = null;
  planImage?: Konva.Image;

  // Configuration
  planConfig: PlanConfig = {
    width: 600,
    height: 400,
    scale: 1,
    draggable: true,
    zoomEnabled: true,
    minZoom: 0.25,
    maxZoom: 3
  };

  // Fullscreen state
  isFullscreen = false;
  private fullscreenChangeHandler = () => this.handleFullscreenChange();
  private resizeObserver?: ResizeObserver;
  private originalImageWidth = 0;
  private originalImageHeight = 0;
  
  // Drag state for sidebar indicators
  private dragState = {
    isDragging: false,
    startX: 0,
    startY: 0,
    currentIndicator: null as RoomIndicator | null,
    dragThreshold: 5, // pixels to move before starting drag
    dragElement: null as HTMLElement | null // Reference to the element being dragged
  };

  // Track badge clicks to prevent drag
  private badgeClickMarkers = new Set<string>(); // Set of marker IDs where badge was clicked
  
  // Track hovered indicator in sidebar
  hoveredIndicatorId: string | null = null;

  planState: PlanState = {
    markers: [],
    selectedMarker: null,
    isDragging: false,
    zoomLevel: 100,
    panOffset: { x: 0, y: 0 },
    showTooltip: false,
    tooltipPosition: { x: 0, y: 0 },
    tooltipMarker: null
  };

  // Symbol shape mapping from environment
  symbolShapeMap: { [key: string]: SymbolShapeConfig } = (environment as any).indikatorSymbolShapeMap || {};

  // Auto-save timeout
  private autoSaveTimeout?: any;

  constructor(
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private objektStrukturService: ObjektStrukturService,
    private planService: PlanService
  ) {}

  ngOnInit(): void {
    // Component initialization
    // Listen for fullscreen changes
    document.addEventListener('fullscreenchange', this.fullscreenChangeHandler);
    document.addEventListener('webkitfullscreenchange', this.fullscreenChangeHandler);
    document.addEventListener('mozfullscreenchange', this.fullscreenChangeHandler);
    document.addEventListener('MSFullscreenChange', this.fullscreenChangeHandler);
  }

  ngAfterViewInit(): void {
    // Initialize Konva after view is initialized, but only if image URL is available
    // The container is conditionally rendered with *ngIf="planImageUrl"
    if (this.planImageUrl) {
      setTimeout(() => {
        this.initializeKonvaIfReady();
      }, 100);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['planImageUrl']) {
      const newUrl = changes['planImageUrl'].currentValue;
      if (newUrl) {
        // If we have a URL but Konva isn't initialized, initialize it
        if (!this.stage) {
          setTimeout(() => {
            this.initializeKonvaIfReady();
          }, 100);
        } else if (this.backgroundLayer) {
          // If already initialized, just load the new image
          this.loadPlanImage().then(() => {
            // After image loads, load existing positions
            if (this.existingPositions && this.existingPositions.length > 0) {
              this.loadExistingPositions();
            }
          });
        }
      } else {
        // Clear existing image if URL is empty
        if (this.planImage && this.backgroundLayer) {
          this.planImage.destroy();
          this.planImage = null as any;
          this.backgroundLayer.draw();
        }
      }
    }
    
    if (changes['existingPositions'] && this.planImage && this.backgroundLayer) {
      this.loadExistingPositions();
    }
    
    // When roomIndicators change, auto-place all active indicators
    if (changes['roomIndicators'] && this.planImage && this.backgroundLayer && this.roomIndicators) {
      // Only auto-place if we don't have existing positions (to avoid duplicates)
      if (!this.existingPositions || this.existingPositions.length === 0) {
        setTimeout(() => {
          this.autoPlaceAllIndicators();
        }, 100);
      }
    }
  }

  ngOnDestroy(): void {
    // Cleanup
    if (this.stage) {
      this.stage.destroy();
    }
    // Remove resize observer
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    // Remove global mouse event listeners
    document.removeEventListener('mousemove', this.handleGlobalMouseMove);
    document.removeEventListener('mouseup', this.handleGlobalMouseUp);
    // Remove fullscreen event listeners
    document.removeEventListener('fullscreenchange', this.fullscreenChangeHandler);
    document.removeEventListener('webkitfullscreenchange', this.fullscreenChangeHandler);
    document.removeEventListener('mozfullscreenchange', this.fullscreenChangeHandler);
    document.removeEventListener('MSFullscreenChange', this.fullscreenChangeHandler);
    // Clear auto-save timeout
    if (this.autoSaveTimeout) {
      clearTimeout(this.autoSaveTimeout);
    }
  }

  private initializeKonvaIfReady(): void {
    // Check if both container and image URL are available
    if (!this.planImageUrl) {
      console.log('Cannot initialize Konva: planImageUrl not set');
      return;
    }

    if (!this.konvaContainer || !this.konvaContainer.nativeElement) {
      console.log('Konva container not available yet, will retry');
      // Retry after a short delay
      setTimeout(() => {
        this.initializeKonvaIfReady();
      }, 200);
      return;
    }

    // Don't re-initialize if already initialized
    if (this.stage) {
      console.log('Konva already initialized');
      return;
    }

    this.initializeKonva();
  }

  private initializeKonva(): void {
    if (!this.konvaContainer || !this.konvaContainer.nativeElement) {
      console.error('Konva container not available');
      return;
    }

    console.log('Initializing Konva with container:', this.konvaContainer.nativeElement);
    
    // Get container dimensions
    const container = this.konvaContainer.nativeElement;
    const containerRect = container.getBoundingClientRect();
    const containerWidth = containerRect.width || this.width;
    const containerHeight = containerRect.height || this.height;
    
    // Update planConfig with actual container dimensions
    this.planConfig.width = containerWidth;
    this.planConfig.height = containerHeight;
    
    // Create stage with container dimensions
    this.stage = new Konva.Stage({
      container: container,
      width: containerWidth,
      height: containerHeight,
      draggable: this.planConfig.draggable
    });

    // Create layers
    this.backgroundLayer = new Konva.Layer();
    this.markersLayer = new Konva.Layer();
    this.uiLayer = new Konva.Layer();

    this.stage.add(this.backgroundLayer);
    this.stage.add(this.markersLayer);
    this.stage.add(this.uiLayer);

    // Add event listeners
    this.setupEventListeners();
    
    // Setup resize observer for responsive canvas
    this.setupResizeObserver();

    // Load image if URL is already available
    if (this.planImageUrl) {
      this.loadPlanImage().then(() => {
        // After image loads, load existing positions or auto-place indicators
        this.loadExistingPositions();
      });
    }
    
    console.log('Konva initialized successfully');
  }
  
  private setupResizeObserver(): void {
    if (!this.konvaContainer || !window.ResizeObserver) {
      return;
    }
    
    this.resizeObserver = new ResizeObserver(() => {
      this.handleContainerResize();
    });
    
    this.resizeObserver.observe(this.konvaContainer.nativeElement);
  }
  
  private handleContainerResize(): void {
    if (!this.stage || !this.konvaContainer) return;
    
    const container = this.konvaContainer.nativeElement;
    const containerRect = container.getBoundingClientRect();
    const newWidth = containerRect.width;
    const newHeight = containerRect.height;
    
    if (newWidth > 0 && newHeight > 0) {
      // Update stage size
      this.stage.width(newWidth);
      this.stage.height(newHeight);
      this.planConfig.width = newWidth;
      this.planConfig.height = newHeight;
      
      // Reload image to fit new dimensions
      if (this.planImageUrl && this.originalImageWidth > 0 && this.originalImageHeight > 0) {
        this.resizePlanImage();
      }
      
      this.stage.batchDraw();
    }
  }
  
  private resizePlanImage(): void {
    if (!this.backgroundLayer || !this.planImage) return;
    
    // Store old image bounds before resize
    const oldImageX = this.planImage.x();
    const oldImageY = this.planImage.y();
    const oldImageWidth = this.planImage.width();
    const oldImageHeight = this.planImage.height();
    
    const containerWidth = this.planConfig.width;
    const containerHeight = this.planConfig.height;
    const imageAspectRatio = this.originalImageWidth / this.originalImageHeight;
    const containerAspectRatio = containerWidth / containerHeight;

    let imageWidth, imageHeight, imageX, imageY;

    if (imageAspectRatio > containerAspectRatio) {
      // Image is wider - fit to width
      imageWidth = containerWidth;
      imageHeight = containerWidth / imageAspectRatio;
      imageX = 0;
      imageY = (containerHeight - imageHeight) / 2;
    } else {
      // Image is taller - fit to height
      imageWidth = containerHeight * imageAspectRatio;
      imageHeight = containerHeight;
      imageX = (containerWidth - imageWidth) / 2;
      imageY = 0;
    }

    // Update image position and size
    this.planImage.x(imageX);
    this.planImage.y(imageY);
    this.planImage.width(imageWidth);
    this.planImage.height(imageHeight);
    
    // Recalculate marker positions based on new image bounds
    this.repositionMarkersAfterResize(oldImageX, oldImageY, oldImageWidth, oldImageHeight);
    
    this.backgroundLayer.batchDraw();
    if (this.markersLayer) {
      this.markersLayer.batchDraw();
    }
  }
  
  private repositionMarkersAfterResize(
    oldImageX: number, 
    oldImageY: number, 
    oldImageWidth: number, 
    oldImageHeight: number
  ): void {
    if (!this.planImage || !this.markersLayer) return;
    
    const newImageX = this.planImage.x();
    const newImageY = this.planImage.y();
    const newImageWidth = this.planImage.width();
    const newImageHeight = this.planImage.height();
    
    // Update all marker positions
    this.planState.markers.forEach(marker => {
      // Get the marker's group from the layer
      const markerGroup = this.markersLayer?.findOne(`#${marker.id}`) as Konva.Group;
      if (!markerGroup) return;
      
      // Calculate old position relative to old image (as percentage)
      const oldRelativeX = (marker.x - oldImageX) / oldImageWidth;
      const oldRelativeY = (marker.y - oldImageY) / oldImageHeight;
      
      // Calculate new absolute position based on new image bounds
      const newAbsoluteX = newImageX + (oldRelativeX * newImageWidth);
      const newAbsoluteY = newImageY + (oldRelativeY * newImageHeight);
      
      // Update marker position
      marker.x = newAbsoluteX;
      marker.y = newAbsoluteY;
      
      // Update Konva group position
      markerGroup.x(newAbsoluteX);
      markerGroup.y(newAbsoluteY);
    });
  }

  private setupEventListeners(): void {
    if (!this.stage) return;
    
    // Stage events
    this.stage.on('wheel', (e) => this.handleZoom(e));
    this.stage.on('dragend', (e) => this.handlePan(e));

    // Drop events (only enable in non-read-only mode)
    if (!this.readOnly) {
      this.stage.on('drop', (e) => this.handleDrop(e));
      this.stage.on('dragover', (e) => this.handleDragOver(e));
    }
  }

  private loadPlanImage(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.planImageUrl || !this.backgroundLayer) {
        console.log('Cannot load plan image:', { planImageUrl: this.planImageUrl, backgroundLayer: !!this.backgroundLayer });
        reject(new Error('Missing planImageUrl or backgroundLayer'));
        return;
      }

      console.log('Loading plan image:', this.planImageUrl);
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => {
        console.log('Plan image loaded successfully');
        // Ensure layers are initialized
        if (!this.backgroundLayer) {
          console.error('Background layer not initialized');
          reject(new Error('Background layer not initialized'));
          return;
        }

        // Store original image dimensions
        this.originalImageWidth = image.width;
        this.originalImageHeight = image.height;

        // Calculate the image dimensions to fit within the container while maintaining aspect ratio
        const containerWidth = this.planConfig.width;
        const containerHeight = this.planConfig.height;
        const imageAspectRatio = image.width / image.height;
        const containerAspectRatio = containerWidth / containerHeight;

        let imageWidth, imageHeight, imageX, imageY;

        if (imageAspectRatio > containerAspectRatio) {
          // Image is wider than container
          imageWidth = containerWidth;
          imageHeight = containerWidth / imageAspectRatio;
          imageX = 0;
          imageY = (containerHeight - imageHeight) / 2;
        } else {
          // Image is taller than container
          imageHeight = containerHeight;
          imageWidth = containerHeight * imageAspectRatio;
          imageX = (containerWidth - imageWidth) / 2;
          imageY = 0;
        }

        this.planImage = new Konva.Image({
          image: image,
          x: imageX,
          y: imageY,
          width: imageWidth,
          height: imageHeight,
          draggable: false,
          listening: true
        });

        this.backgroundLayer.add(this.planImage);
        this.backgroundLayer.draw();
        resolve();
      };
      image.onerror = () => {
        console.error('Failed to load plan image:', this.planImageUrl);
        reject(new Error('Failed to load plan image'));
      };
      image.src = this.planImageUrl;
    });
  }

  // Zoom functionality
  private handleZoom(e: KonvaEventObject<WheelEvent>): void {
    e.evt.preventDefault();

    const scaleBy = 1.1;
    const stage = e.target.getStage();
    if (!stage) return;

    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();

    if (!pointer) return;

    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };

    const newScale = e.evt.deltaY > 0 ? oldScale * scaleBy : oldScale / scaleBy;
    const clampedScale = Math.max(this.planConfig.minZoom, Math.min(this.planConfig.maxZoom, newScale));

    this.planState.zoomLevel = clampedScale * 100;
    this.zoomChanged.emit(this.planState.zoomLevel);

    stage.scale({ x: clampedScale, y: clampedScale });

    const newPos = {
      x: pointer.x - mousePointTo.x * clampedScale,
      y: pointer.y - mousePointTo.y * clampedScale,
    };

    stage.position(newPos);
    stage.batchDraw();
  }

  private handlePan(e: KonvaEventObject<DragEvent>): void {
    this.planState.panOffset = {
      x: e.target.x(),
      y: e.target.y()
    };
  }

  // Drag and drop functionality
  private handleDragOver(e: KonvaEventObject<DragEvent>): void {
    e.evt.preventDefault();
  }

  private handleDrop(e: KonvaEventObject<DragEvent>): void {
    e.evt.preventDefault();
    
    const data = e.evt.dataTransfer?.getData('text/plain');
    if (!data) return;

    try {
      const indicator: RoomIndicator = JSON.parse(data);
      if (!this.stage) return;
      const position = this.stage.getPointerPosition();
      
      if (position) {
        // Place marker
        const marker = this.placeMarker(indicator, position.x, position.y);
        
        // Auto-save position (convert to original image pixels)
        const originalPixels = this.convertCanvasPositionToOriginalPixels(position.x, position.y);
        this.saveMarkerPosition(marker, { x: originalPixels.x, y: originalPixels.y });
      }
    } catch (error) {
      console.error('Error parsing dropped data:', error);
    }
  }

  public placeMarker(indicator: RoomIndicator, x: number, y: number): PlanMarker {
    // Disable placing markers in read-only mode
    if (this.readOnly) {
      throw new Error('Cannot place markers in read-only mode');
    }
    const marker: PlanMarker = {
      id: `marker_${Date.now()}_${indicator.id}`,
      indicator: indicator,
      x: x,
      y: y,
      scale: 1,
      rotation: 0,
      visible: true,
      locked: false,
      shape: indicator.indikator?.symbol_id || '1'
    };

    this.planState.markers.push(marker);
    this.createMarkerShape(marker);
    this.markerPlaced.emit(marker);
    
    this.snackBar.open(`Indikator "${indicator.name}" wurde platziert`, 'Schließen', { duration: 2000 });
    
    return marker;
  }

  private createMarkerShape(marker: PlanMarker): void {
    const group = new Konva.Group({
      id: marker.id,
      x: marker.x,
      y: marker.y,
      draggable: !this.readOnly // Disable dragging in read-only mode
    });

    const shape = this.createShape(marker);
    group.add(shape);

    // Add number badge
    const badge = this.createNumberBadge(marker);
    if (badge) {
      // Make badge non-draggable (it will still move with parent group)
      badge.draggable(false);
      // Track mousedown on badge to prevent drag
      badge.on('mousedown', (e) => {
        e.cancelBubble = true;
        e.evt.stopPropagation();
        // Mark this marker as having badge click
        this.badgeClickMarkers.add(marker.id);
        // Temporarily disable group dragging
        group.draggable(false);
      });
      // Add click handler to badge that opens dialog
      badge.on('click', (e) => {
        e.cancelBubble = true;
        e.evt.stopPropagation();
        // Remove from badge click set
        this.badgeClickMarkers.delete(marker.id);
        // Re-enable dragging for future interactions
        group.draggable(!this.readOnly); // Disable dragging in read-only mode
        // Open dialog
        this.showIndicatorDialog(marker);
      });
      group.add(badge);
    }

    // Add event listeners to group (for symbol area)
    group.on('dragstart', (e) => {
      // Prevent drag if badge was clicked
      if (this.badgeClickMarkers.has(marker.id)) {
        e.evt.preventDefault();
        e.cancelBubble = true;
        return;
      }
    });
    group.on('dragend', (e) => {
      // Clear badge click marker after drag
      this.badgeClickMarkers.delete(marker.id);
      this.handleMarkerDragEnd(e, marker);
    });
    group.on('click', (e) => {
      // Only handle click if it wasn't on the badge or its children
      const clickedNode = e.target;
      const isBadgeClick = clickedNode === badge || 
                          (clickedNode.getParent && clickedNode.getParent() === badge) ||
                          (badge && badge.findOne((node) => node === clickedNode));
      if (!isBadgeClick && !this.badgeClickMarkers.has(marker.id)) {
        this.handleMarkerClick(e, marker);
      } else {
        // Clear badge click marker
        this.badgeClickMarkers.delete(marker.id);
      }
    });
    group.on('mouseenter', (e) => this.handleMarkerHover(e, marker));
    group.on('mouseleave', (e) => this.handleMarkerLeave(e));
    group.on('contextmenu', (e) => this.handleMarkerRightClick(e, marker));

    if (this.markersLayer) {
      this.markersLayer.add(group);
      this.markersLayer.draw();
    }
  }

  private createNumberBadge(marker: PlanMarker): Konva.Group | null {
    const indicatorNumber = marker.indicator.indikator_number;
    // Always create a badge, even if there's no number (show "-")
    const formattedNumber = this.getFormattedIndicatorNumber(indicatorNumber);
    const config = this.getSymbolConfig(marker.shape);
    const halfSize = config.size / 2;
    
    // Badge dimensions - scaled to 50% of original
    const badgeWidth = 14; // 28 * 0.5
    const badgeHeight = 8; // 16 * 0.5
    const cornerRadius = 2; // 4 * 0.5
    
    // Badge position: centered below the symbol
    const badgeX = -badgeWidth / 2; // Center horizontally
    const badgeY = halfSize + 2; // Below the symbol with small gap (4 * 0.5)
    
    const badgeGroup = new Konva.Group({
      x: badgeX,
      y: badgeY
    });

    // Background rectangle with rounded corners
    const badgeBackground = new Konva.Rect({
      width: badgeWidth,
      height: badgeHeight,
      fill: '#3b82f6', // Blue background
      stroke: '#ffffff',
      strokeWidth: 0.75, // 1.5 * 0.5
      cornerRadius: cornerRadius,
      shadowColor: 'black',
      shadowBlur: 1.5, // 3 * 0.5
      shadowOffset: { x: 0.5, y: 0.5 }, // 1 * 0.5
      shadowOpacity: 0.5
    });

    // Text for the number - properly centered
    const badgeText = new Konva.Text({
      text: formattedNumber,
      fontSize: 7, // Slightly larger than 50% (10 * 0.5 = 5) for readability, but still smaller
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      fill: '#ffffff',
      align: 'center',
      verticalAlign: 'middle',
      x: 0,
      y: (badgeHeight - 7) / 2, // Center vertically (approximate for font size 7)
      width: badgeWidth,
      height: badgeHeight
    });

    badgeGroup.add(badgeBackground);
    badgeGroup.add(badgeText);

    return badgeGroup;
  }

  private createShape(marker: PlanMarker): Konva.Group {
    const config = this.getSymbolConfig(marker.shape);
    const halfSize = config.size / 2;
    const symbolUrl = this.getIndicatorSymbolUrl(marker.indicator);
    const symbolColor = this.getIndicatorSymbolColor(marker.indicator) || (config as any).color || config.stroke || '#3b82f6';
    
    // Use SVG with color if available, otherwise fall back to basic shapes
    if (symbolUrl) {
      return this.createColoredSVGShape(marker, config, symbolUrl, symbolColor, halfSize);
    }
    
    // Fallback to basic shapes
    return this.createBasicShape(marker, config, halfSize);
  }

  private createColoredSVGShape(marker: PlanMarker, config: SymbolShapeConfig, symbolUrl: string, symbolColor: string, halfSize: number): Konva.Group {
    const group = new Konva.Group({
      x: -halfSize,
      y: -halfSize,
      width: config.size,
      height: config.size
    });
    
    // Create temporary canvas to render colored SVG
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = config.size;
    tempCanvas.height = config.size;
    const ctx = tempCanvas.getContext('2d');
    
    if (ctx) {
      // Load SVG
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => {
        // Clear canvas
        ctx.clearRect(0, 0, config.size, config.size);
        
        // Draw SVG to canvas
        ctx.drawImage(image, 0, 0, config.size, config.size);
        
        // Apply color using composite operation
        ctx.globalCompositeOperation = 'source-atop';
        ctx.fillStyle = symbolColor;
        ctx.fillRect(0, 0, config.size, config.size);
        
        // Reset composite operation
        ctx.globalCompositeOperation = 'source-over';
        
        // Create Konva image from canvas
        const coloredImage = new Konva.Image({
          image: tempCanvas,
          width: config.size,
          height: config.size,
          shadowColor: 'black',
          shadowBlur: 4,
          shadowOffset: { x: 2, y: 2 },
          shadowOpacity: 0.3
        });
        
        group.destroyChildren();
        group.add(coloredImage);
        group.draw();
      };
      image.onerror = () => {
        console.error('Failed to load SVG:', symbolUrl);
        // Fallback to colored circle
        const fallback = new Konva.Circle({
          radius: halfSize,
          fill: symbolColor,
          opacity: 0.8,
          stroke: '#ffffff',
          strokeWidth: 2,
          shadowColor: 'black',
          shadowBlur: 4,
          shadowOffset: { x: 2, y: 2 },
          shadowOpacity: 0.3
        });
        group.destroyChildren();
        group.add(fallback);
        group.draw();
      };
      image.src = symbolUrl;
    }
    
    // Placeholder while loading
    const placeholder = new Konva.Circle({
      radius: halfSize,
      fill: symbolColor,
      opacity: 0.6,
      stroke: '#ffffff',
      strokeWidth: 2
    });
    group.add(placeholder);
    
    return group;
  }

  private createBasicShape(marker: PlanMarker, config: SymbolShapeConfig, halfSize: number): Konva.Group {
    const group = new Konva.Group({
      x: -halfSize,
      y: -halfSize,
      width: config.size,
      height: config.size
    });
    
    let shape: Konva.Shape;
    
    switch (config.shape) {
      case 'circle':
        shape = new Konva.Circle({
          radius: halfSize,
          fill: config.fill,
          stroke: config.stroke,
          strokeWidth: config.strokeWidth,
          shadowColor: 'black',
          shadowBlur: 4,
          shadowOffset: { x: 2, y: 2 },
          shadowOpacity: 0.3
        });
        break;

      case 'rect':
        shape = new Konva.Rect({
          width: config.size,
          height: config.size,
          fill: config.fill,
          stroke: config.stroke,
          strokeWidth: config.strokeWidth,
          cornerRadius: 4,
          shadowColor: 'black',
          shadowBlur: 4,
          shadowOffset: { x: 2, y: 2 },
          shadowOpacity: 0.3
        });
        break;

      case 'polygon':
        shape = new Konva.Line({
          points: config.points || [0, -halfSize, halfSize, halfSize, -halfSize, halfSize],
          closed: true,
          fill: config.fill,
          stroke: config.stroke,
          strokeWidth: config.strokeWidth,
          shadowColor: 'black',
          shadowBlur: 4,
          shadowOffset: { x: 2, y: 2 },
          shadowOpacity: 0.3
        });
        break;

      default:
        shape = new Konva.Circle({
          radius: halfSize,
          fill: '#ffffff',
          stroke: '#6b7280',
          strokeWidth: 2
        });
    }
    
    group.add(shape);
    return group;
  }

  private getSymbolConfig(symbolId: string): SymbolShapeConfig {
    const baseConfig = this.symbolShapeMap[symbolId] || this.symbolShapeMap['1'];
    // Scale down to 50% of original size
    return {
      ...baseConfig,
      size: baseConfig.size * 0.5,
      strokeWidth: baseConfig.strokeWidth * 0.5
    };
  }

  // Event handlers
  private handleMarkerDragEnd(e: KonvaEventObject<DragEvent>, marker: PlanMarker): void {
    const newX = e.target.x();
    const newY = e.target.y();
    
    // Update marker position
    marker.x = newX;
    marker.y = newY;
    
    // Convert to original image pixels
    const originalPixels = this.convertCanvasPositionToOriginalPixels(newX, newY);
    
    // Auto-save position
    this.saveMarkerPosition(marker, { x: originalPixels.x, y: originalPixels.y });
    
    // Emit event for parent component
    this.markerMoved.emit(marker);
  }

  private handleMarkerClick(e: KonvaEventObject<MouseEvent>, marker: PlanMarker): void {
    e.evt.stopPropagation();
    // Show detailed dialog on click
    this.showIndicatorDialog(marker);
  }

  private handleMarkerHover(e: KonvaEventObject<MouseEvent>, marker: PlanMarker): void {
    // Show quick tooltip on hover
    if (this.stage) {
      const pointerPos = this.stage.getPointerPosition();
      if (pointerPos) {
        // Convert stage coordinates to screen coordinates
        const container = this.stage.container();
        const containerRect = container.getBoundingClientRect();
        const screenX = containerRect.left + pointerPos.x;
        const screenY = containerRect.top + pointerPos.y;
        
        this.planState.tooltipMarker = marker;
        this.planState.tooltipPosition = { x: screenX, y: screenY };
        this.planState.showTooltip = true;
      }
    }
  }

  private handleMarkerLeave(e: KonvaEventObject<MouseEvent>): void {
    // Hide tooltip when mouse leaves marker
    // Small delay to allow moving to tooltip
    setTimeout(() => {
      if (!this.planState.tooltipMarker || 
          !this.isMouseOverTooltip()) {
        this.hideTooltip();
      }
    }, 100);
  }

  private isMouseOverTooltip(): boolean {
    // Check if mouse is over tooltip element
    const tooltipElement = document.querySelector('.tooltip');
    if (!tooltipElement) return false;
    
    const rect = tooltipElement.getBoundingClientRect();
    const mouseX = this.planState.tooltipPosition.x;
    const mouseY = this.planState.tooltipPosition.y;
    
    return mouseX >= rect.left && mouseX <= rect.right &&
           mouseY >= rect.top && mouseY <= rect.bottom;
  }

  private handleMarkerRightClick(e: KonvaEventObject<MouseEvent>, marker: PlanMarker): void {
    e.evt.preventDefault();
    this.removeMarker(marker.id);
  }

  private showTooltip(event: MouseEvent, marker: PlanMarker): void {
    this.planState.tooltipMarker = marker;
    this.planState.tooltipPosition = { x: event.clientX, y: event.clientY };
    this.planState.showTooltip = true;
  }

  private showIndicatorDialog(marker: PlanMarker): void {
    // Create a detailed dialog component (we'll create this)
    // For now, we can use a simple approach with MatDialog
    const dialogRef = this.dialog.open(IndicatorDetailDialogComponent, {
      width: '500px',
      maxWidth: '90vw',
      data: {
        marker: marker,
        indicator: marker.indicator
      },
      panelClass: 'indicator-detail-dialog'
    });

    dialogRef.afterClosed().subscribe(() => {
      // Dialog closed
    });
  }

  public hideTooltip(): void {
    this.planState.showTooltip = false;
    this.planState.tooltipMarker = null;
  }

  public removeMarker(markerId: string): void {
    const marker = this.planState.markers.find(m => m.id === markerId);
    if (marker) {
      // Remove from Konva stage
      const konvaMarker = this.stage?.findOne(`#${markerId}`);
      if (konvaMarker) {
        konvaMarker.destroy();
      }

      // Remove from state
      const index = this.planState.markers.findIndex(m => m.id === markerId);
      if (index > -1) {
        this.planState.markers.splice(index, 1);
      }

      this.markerRemoved.emit(markerId);
      this.snackBar.open(`Indikator "${marker.indicator.name}" wurde entfernt`, 'Schließen', { duration: 2000 });
    }
  }

  // Public methods for external control
  public zoomIn(): void {
    if (!this.stage) return;
    const newScale = Math.min(this.stage.scaleX() * 1.25, this.planConfig.maxZoom);
    this.updateZoom(newScale);
  }

  public zoomOut(): void {
    if (!this.stage) return;
    const newScale = Math.max(this.stage.scaleX() * 0.8, this.planConfig.minZoom);
    this.updateZoom(newScale);
  }

  public resetZoom(): void {
    if (!this.stage) return;
    this.updateZoom(1);
  }

  public toggleFullscreen(): void {
    if (!this.interactivePlanContainer) return;

    const container = this.interactivePlanContainer.nativeElement;

    if (!this.isFullscreen) {
      // Enter fullscreen
      if (container.requestFullscreen) {
        container.requestFullscreen();
      } else if ((container as any).webkitRequestFullscreen) {
        (container as any).webkitRequestFullscreen();
      } else if ((container as any).mozRequestFullScreen) {
        (container as any).mozRequestFullScreen();
      } else if ((container as any).msRequestFullscreen) {
        (container as any).msRequestFullscreen();
      }
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      } else if ((document as any).mozCancelFullScreen) {
        (document as any).mozCancelFullScreen();
      } else if ((document as any).msExitFullscreen) {
        (document as any).msExitFullscreen();
      }
    }
  }

  private handleFullscreenChange(): void {
    const isFullscreen = !!(
      document.fullscreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).mozFullScreenElement ||
      (document as any).msFullscreenElement
    );

    this.isFullscreen = isFullscreen;

    // Resize Konva stage when entering/exiting fullscreen
    if (this.stage) {
      setTimeout(() => {
        this.resizeStageForFullscreen();
      }, 100);
    }
  }

  private resizeStageForFullscreen(): void {
    // The ResizeObserver will handle the resize automatically
    // This method is kept for compatibility but the actual resize is handled by handleContainerResize
    if (this.stage && this.konvaContainer) {
      // Trigger a manual resize check
      setTimeout(() => {
        this.handleContainerResize();
      }, 50);
    }
  }

  private updateZoom(scale: number): void {
    if (!this.stage) return;
    this.stage.scale({ x: scale, y: scale });
    this.planState.zoomLevel = scale * 100;
    this.zoomChanged.emit(this.planState.zoomLevel);
    this.stage.batchDraw();
  }

  public exportAsImage(format: 'png' | 'jpeg' = 'png', exportFullContent: boolean = true): string {
    if (!this.stage) return '';
    
    if (exportFullContent && this.planImage) {
      // Export full content area regardless of zoom/pan
      return this.exportFullContent(format);
    } else {
      // Original behavior - export visible area only
      const dataUrl = this.stage.toDataURL({
        mimeType: `image/${format}`,
        quality: 0.9
      });
      this.planExported.emit(dataUrl);
      return dataUrl;
    }
  }

  private exportFullContent(format: 'png' | 'jpeg'): string {
    if (!this.stage || !this.planImage || !this.backgroundLayer || !this.markersLayer) {
      return '';
    }

    // Store current state
    const currentScale = this.stage.scaleX();
    const currentPosition = this.stage.position();
    const currentZoomLevel = this.planState.zoomLevel;

    try {
      // Get full content bounds
      const fullBounds = this.calculateFullContentBounds();
      
      // Temporarily reset transform to base state (scale 1.0, position 0,0)
      this.stage.scale({ x: 1, y: 1 });
      this.stage.position({ x: 0, y: 0 });
      this.stage.batchDraw();

      // Export full area with higher quality
      const dataUrl = this.stage.toDataURL({
        mimeType: `image/${format}`,
        quality: 0.9,
        x: fullBounds.x,
        y: fullBounds.y,
        width: fullBounds.width,
        height: fullBounds.height,
        pixelRatio: 2 // Higher quality export
      });

      // Restore original transform
      this.stage.scale({ x: currentScale, y: currentScale });
      this.stage.position(currentPosition);
      this.stage.batchDraw();
      this.planState.zoomLevel = currentZoomLevel;

      this.planExported.emit(dataUrl);
      return dataUrl;
    } catch (error) {
      console.error('Error exporting full content:', error);
      // Restore state even on error
      this.stage.scale({ x: currentScale, y: currentScale });
      this.stage.position(currentPosition);
      this.stage.batchDraw();
      this.planState.zoomLevel = currentZoomLevel;
      return '';
    }
  }

  private calculateFullContentBounds(): { x: number, y: number, width: number, height: number } {
    if (!this.planImage) {
      return { x: 0, y: 0, width: this.planConfig.width, height: this.planConfig.height };
    }

    // Get image bounds in stage coordinates
    const imageRect = this.planImage.getClientRect();
    
    // Start with image bounds
    let minX = imageRect.x;
    let minY = imageRect.y;
    let maxX = imageRect.x + imageRect.width;
    let maxY = imageRect.y + imageRect.height;

    // Check all markers to see if they extend beyond image bounds
    if (this.markersLayer) {
      this.markersLayer.find('Group').forEach((markerGroup) => {
        try {
          const markerRect = markerGroup.getClientRect();
          if (markerRect && !isNaN(markerRect.x) && !isNaN(markerRect.y)) {
            minX = Math.min(minX, markerRect.x);
            minY = Math.min(minY, markerRect.y);
            maxX = Math.max(maxX, markerRect.x + markerRect.width);
            maxY = Math.max(maxY, markerRect.y + markerRect.height);
          }
        } catch (error) {
          // Skip markers that can't be measured
          console.warn('Could not get bounds for marker:', error);
        }
      });
    }

    // Add small padding to ensure nothing is cut off
    const padding = 10;
    minX = Math.max(0, minX - padding);
    minY = Math.max(0, minY - padding);
    maxX = Math.min(this.stage?.width() || maxX, maxX + padding);
    maxY = Math.min(this.stage?.height() || maxY, maxY + padding);

    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY
    };
  }

  public exportAsJSON(): string {
    const data = {
      markers: this.planState.markers,
      stageConfig: {
        scale: this.stage?.scaleX() || 1,
        position: this.stage?.position() || { x: 0, y: 0 }
      },
      metadata: {
        exportedAt: new Date().toISOString(),
        version: '1.0'
      }
    };
    return JSON.stringify(data, null, 2);
  }

  public getFormattedIndicatorNumber(indikatorNumber: number | null | undefined): string {
    if (indikatorNumber === null || indikatorNumber === undefined) {
      return '-';
    }
    return indikatorNumber.toString().padStart(3, '0');
  }

  // Drag and Drop Methods for Sidebar Indicators
  onMouseDown(event: MouseEvent, indicator: RoomIndicator): void {
    // In read-only mode, handle click directly for highlighting
    if (this.readOnly) {
      event.preventDefault();
      event.stopPropagation();
      // Directly trigger highlight on click in read-only mode
      this.openIndicatorDialogFromSidebar(indicator);
      return;
    }
    // Prevent default to stop text selection
    event.preventDefault();
    
    this.dragState.isDragging = false;
    this.dragState.startX = event.clientX;
    this.dragState.startY = event.clientY;
    this.dragState.currentIndicator = indicator;
    this.dragState.dragElement = event.currentTarget as HTMLElement;
    
    // Initially disable drag
    if (this.dragState.dragElement) {
      this.dragState.dragElement.setAttribute('draggable', 'false');
    }
    
    // Add global mouse move and up listeners
    document.addEventListener('mousemove', this.handleGlobalMouseMove);
    document.addEventListener('mouseup', this.handleGlobalMouseUp);
  }

  private handleGlobalMouseMove = (event: MouseEvent): void => {
    if (!this.dragState.currentIndicator || !this.dragState.dragElement) return;
    
    const deltaX = Math.abs(event.clientX - this.dragState.startX);
    const deltaY = Math.abs(event.clientY - this.dragState.startY);
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    
    // If mouse moved beyond threshold, enable drag
    if (distance > this.dragState.dragThreshold && !this.dragState.isDragging) {
      this.dragState.isDragging = true;
      // Enable drag on the element
      this.dragState.dragElement.setAttribute('draggable', 'true');
    }
  };

  private handleGlobalMouseUp = (event: MouseEvent): void => {
    // Remove global listeners
    document.removeEventListener('mousemove', this.handleGlobalMouseMove);
    document.removeEventListener('mouseup', this.handleGlobalMouseUp);
    
    // Capture the indicator before resetting state
    const clickedIndicator = this.dragState.currentIndicator;
    const wasDragging = this.dragState.isDragging;
    
    // Reset drag state
    this.dragState.isDragging = false;
    this.dragState.currentIndicator = null;
    this.dragState.dragElement = null;
    
    // If it was a click (not a drag) and we have a valid indicator, open the dialog
    if (!wasDragging && clickedIndicator && clickedIndicator.id) {
      // Small delay to ensure any drag events have been processed
      setTimeout(() => {
        this.openIndicatorDialogFromSidebar(clickedIndicator);
      }, 50);
    }
  };

  onClick(event: MouseEvent, indicator: RoomIndicator): void {
    // In read-only mode, handle click directly for highlighting
    if (this.readOnly) {
      event.preventDefault();
      event.stopPropagation();
      this.openIndicatorDialogFromSidebar(indicator);
      return;
    }
    
    // Only handle click if it wasn't a drag and indicator is valid
    if (!indicator || !indicator.id) return;
    
    if (!this.dragState.isDragging && this.dragState.currentIndicator?.id === indicator.id) {
      event.preventDefault();
      event.stopPropagation();
      // Dialog will be opened by handleGlobalMouseUp
    }
  }

  onDragStart(event: DragEvent, indicator: RoomIndicator): void {
    // Disable drag in read-only mode
    if (this.readOnly) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    // Only allow drag if we've detected movement
    if (!this.dragState.isDragging) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return;
    }
    
    // Remove mouse move listener
    document.removeEventListener('mousemove', this.handleGlobalMouseMove);
    
    if (event.dataTransfer) {
      event.dataTransfer.setData('text/plain', JSON.stringify(indicator));
      event.dataTransfer.effectAllowed = 'copy';
    }
    
    // Reset drag state after drag starts
    this.dragState.isDragging = false;
    this.dragState.currentIndicator = null;
    this.dragState.dragElement = null;
  }

  private openIndicatorDialogFromSidebar(indicator: RoomIndicator): void {
    if (!indicator || !indicator.id) {
      console.error('Invalid indicator provided to openIndicatorDialogFromSidebar');
      return;
    }

    // Find if this indicator is already placed on the canvas
    const existingMarker = this.planState.markers.find(m => m.indicator && m.indicator.id === indicator.id);
    
    if (existingMarker) {
      // Highlight the marker on the plan (no dialog)
      this.highlightMarker(existingMarker);
    }
    // If marker doesn't exist on canvas, do nothing (no dialog, no highlight)
  }

  /**
   * Open indicator detail dialog from the Detail button
   */
  openIndicatorDetailDialog(indicator: RoomIndicator): void {
    if (!indicator || !indicator.id) {
      console.error('Invalid indicator provided to openIndicatorDetailDialog');
      return;
    }

    // Find if this indicator is already placed on the canvas
    const existingMarker = this.planState.markers.find(m => m.indicator && m.indicator.id === indicator.id);
    
    if (existingMarker) {
      // If marker exists, show dialog for it
      this.showIndicatorDialog(existingMarker);
    } else {
      // If not placed, create a temporary marker for dialog display
      const tempMarker: PlanMarker = {
        id: `temp_${indicator.id}`,
        indicator: indicator,
        x: 0,
        y: 0,
        scale: 1,
        rotation: 0,
        visible: true,
        locked: false,
        shape: indicator.indikator?.symbol_id || '1'
      };
      this.showIndicatorDialog(tempMarker);
    }
  }

  /**
   * Highlight a marker on the plan for 1 second
   */
  private highlightMarker(marker: PlanMarker): void {
    if (!this.markersLayer || !marker) return;

    // Find the marker group on the canvas
    const markerGroup = this.markersLayer.findOne(`#${marker.id}`) as Konva.Group;
    if (!markerGroup) return;

    // Get the config to determine size
    const config = this.getSymbolConfig(marker.shape);
    const highlightRadius = config.size * 1.5; // Make highlight larger than symbol

    // Create a pulsing highlight circle with orange fill and blur
    const highlight = new Konva.Circle({
      radius: highlightRadius,
      fill: 'rgba(249, 115, 22, 0.4)', // Orange with transparency (rgba(249, 115, 22) is orange-500)
      x: 0,
      y: 0,
      listening: false, // Don't interfere with mouse events
      shadowColor: 'rgba(249, 115, 22, 0.8)', // Orange shadow
      shadowBlur: 15, // Blur effect
      shadowOffset: { x: 0, y: 0 },
      shadowOpacity: 1
    });

    // Add highlight to the marker group (so it moves with the marker)
    // Add it at the beginning so it's behind the symbol but visible
    markerGroup.add(highlight);
    highlight.moveToBottom(); // Put highlight behind the symbol
    markerGroup.moveToTop(); // Bring entire marker group to front

    // Create pulsing animation using requestAnimationFrame
    let startTime = Date.now();
    const duration = 1000; // 1 second
    let animationId: number;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = elapsed / duration;

      if (progress < 1) {
        // Pulse effect: scale between 0.8 and 1.2
        const pulse = Math.sin(progress * Math.PI * 4) * 0.2 + 1;
        highlight.scale({ x: pulse, y: pulse });
        // Fade out
        highlight.opacity(0.6 * (1 - progress));
        // Increase blur as it fades
        highlight.shadowBlur(15 + (progress * 10));
        
        this.markersLayer?.draw();
        animationId = requestAnimationFrame(animate);
      } else {
        // Animation complete - remove highlight
        highlight.destroy();
        this.markersLayer?.draw();
      }
    };

    animationId = requestAnimationFrame(animate);

    // Safety: ensure highlight is removed after 1.1 seconds even if animation doesn't complete
    setTimeout(() => {
      if (highlight && highlight.parent) {
        cancelAnimationFrame(animationId);
        highlight.destroy();
        this.markersLayer?.draw();
      }
    }, 1100);
  }

  // Helper methods for indicator display
  getIndicatorSymbolUrl(indicator: RoomIndicator): string | null {
    if (!indicator.indikator?.symbol_id) return null;
    const key = (environment as any).indikatorSymbolMap?.[indicator.indikator.symbol_id];
    if (!key) return null;
    const base = (environment as any).indikatorSymbolBasePath || '/assets/symbols';
    return `${base}/${key}.svg`;
  }

  getIndicatorSymbolColor(indicator: RoomIndicator): string | null {
    if (!indicator.indikator?.symbol_id) return null;
    const colorMap = (environment as any).indikatorSymbolColorMap || {};
    return colorMap[indicator.indikator.symbol_id] || null;
  }

  getIndicatorStatusTextSimple(status: number | boolean): string {
    const statusValue = status as any; // API can return boolean or number
    return (statusValue === 1 || statusValue === true) ? 'Aktiv' : 'Inaktiv';
  }

  getIndicatorStatusBadgeClasses(status: number | boolean): string {
    const statusValue = status as any; // API can return boolean or number
    if (statusValue === 1 || statusValue === true) {
      return 'bg-green-100 text-green-800';
    }
    return 'bg-red-100 text-red-800';
  }

  formatSchwellenwertAsInteger(schwellenwert: number | null | undefined): string {
    if (schwellenwert === null || schwellenwert === undefined) {
      return '-';
    }
    return Math.round(schwellenwert).toString();
  }

  // Expose Math to template
  Math = Math;

  /**
   * Get sorted room indicators by indikator_number
   */
  getSortedRoomIndicators(): RoomIndicator[] {
    if (!this.roomIndicators || this.roomIndicators.length === 0) {
      return [];
    }
    return this.sortIndicatorsByNumber(this.roomIndicators);
  }

  private sortIndicatorsByNumber(indicators: RoomIndicator[]): RoomIndicator[] {
    return [...indicators].sort((a, b) => {
      const numA = a.indikator_number ?? 999999;
      const numB = b.indikator_number ?? 999999;
      return numA - numB;
    });
  }

  // Method to update the plan image URL (called from parent component)
  updatePlanImageUrl(newUrl: string): void {
    // Prevent unnecessary updates if URL hasn't changed
    if (!newUrl || newUrl === this.planImageUrl) {
      return;
    }
    
    console.log('updatePlanImageUrl called:', { newUrl, currentUrl: this.planImageUrl, backgroundLayer: !!this.backgroundLayer });
    this.planImageUrl = newUrl;
    
    // If background layer is ready, load the image immediately
    if (this.backgroundLayer) {
      this.loadPlanImage().then(() => {
        if (this.existingPositions && this.existingPositions.length > 0) {
          this.loadExistingPositions();
        }
      });
    } else {
      // If not ready, try to initialize Konva
      if (!this.stage) {
        console.log('Stage not initialized, trying to initialize Konva');
        setTimeout(() => {
          this.initializeKonvaIfReady();
        }, 100);
      } else {
        // Stage exists but background layer doesn't - wait a bit and try loading
        setTimeout(() => {
          if (this.backgroundLayer && this.planImageUrl === newUrl) {
            this.loadPlanImage().then(() => {
              if (this.existingPositions && this.existingPositions.length > 0) {
                this.loadExistingPositions();
              }
            });
          }
        }, 200);
      }
    }
  }

  // Public method to force re-initialization (can be called from parent)
  public reinitialize(): void {
    console.log('Force re-initializing Konva');
    if (this.stage) {
      this.stage.destroy();
      this.stage = null;
      this.backgroundLayer = null;
      this.markersLayer = null;
      this.uiLayer = null;
      this.planImage = undefined;
    }
    setTimeout(() => {
      this.initializeKonvaIfReady();
    }, 100);
  }

  // Coordinate conversion methods
  /**
   * Convert pixel position from original image to absolute position on scaled canvas
   * @param originalPixelX - X position in pixels from original image (top-left = 0,0)
   * @param originalPixelY - Y position in pixels from original image (top-left = 0,0)
   * @returns Absolute position on the Konva canvas
   */
  private convertOriginalPixelsToCanvasPosition(originalPixelX: number, originalPixelY: number): { x: number, y: number } {
    if (!this.planImage || this.originalImageWidth === 0 || this.originalImageHeight === 0) {
      return { x: 0, y: 0 };
    }
    
    // Get scaled image bounds and position
    const imageX = this.planImage.x();
    const imageY = this.planImage.y();
    const scaledImageWidth = this.planImage.width();
    const scaledImageHeight = this.planImage.height();
    
    // Calculate scale factors (how much the image was scaled down/up)
    const scaleX = scaledImageWidth / this.originalImageWidth;
    const scaleY = scaledImageHeight / this.originalImageHeight;
    
    // Scale the original pixel position to match the scaled image
    const scaledX = originalPixelX * scaleX;
    const scaledY = originalPixelY * scaleY;
    
    // Add the image offset (image might be centered in container)
    const absoluteX = imageX + scaledX;
    const absoluteY = imageY + scaledY;
    
    return { x: absoluteX, y: absoluteY };
  }

  /**
   * Convert absolute canvas position to pixel position on original image
   * @param canvasX - X position on the Konva canvas
   * @param canvasY - Y position on the Konva canvas
   * @returns Pixel position relative to original image (top-left = 0,0)
   */
  private convertCanvasPositionToOriginalPixels(canvasX: number, canvasY: number): { x: number, y: number } {
    if (!this.planImage || this.originalImageWidth === 0 || this.originalImageHeight === 0) {
      return { x: 0, y: 0 };
    }
    
    // Get scaled image bounds and position
    const imageX = this.planImage.x();
    const imageY = this.planImage.y();
    const scaledImageWidth = this.planImage.width();
    const scaledImageHeight = this.planImage.height();
    
    // Calculate scale factors
    const scaleX = scaledImageWidth / this.originalImageWidth;
    const scaleY = scaledImageHeight / this.originalImageHeight;
    
    // Get position relative to the scaled image (subtract image offset)
    const relativeToScaledImageX = canvasX - imageX;
    const relativeToScaledImageY = canvasY - imageY;
    
    // Convert back to original image pixels (divide by scale factor)
    const originalPixelX = relativeToScaledImageX / scaleX;
    const originalPixelY = relativeToScaledImageY / scaleY;
    
    // Clamp to original image bounds
    return {
      x: Math.max(0, Math.min(this.originalImageWidth, originalPixelX)),
      y: Math.max(0, Math.min(this.originalImageHeight, originalPixelY))
    };
  }

  // This method is kept for backward compatibility if needed elsewhere
  private convertToRelativePosition(x: number, y: number): { x: number, y: number } {
    // Convert to original image pixels first
    const originalPixels = this.convertCanvasPositionToOriginalPixels(x, y);
    
    // Then convert to percentage if needed
    const relativeX = (originalPixels.x / this.originalImageWidth) * 100;
    const relativeY = (originalPixels.y / this.originalImageHeight) * 100;
    
    return {
      x: Math.max(0, Math.min(100, relativeX)),
      y: Math.max(0, Math.min(100, relativeY))
    };
  }

  // Save marker position to backend
  private saveMarkerPosition(marker: PlanMarker, relativePosition: { x: number, y: number }): void {
    // Save position directly to room_indikator table
    const roomIndikatorId = marker.indicator.id;
    if (!roomIndikatorId) {
      console.warn('Cannot save marker position: room_indikator ID not found');
      return;
    }
    
    // Convert canvas position to original image pixels
    const originalPixels = this.convertCanvasPositionToOriginalPixels(
      marker.x,  // Current absolute position on canvas
      marker.y   // Current absolute position on canvas
    );
    
    // Update the room_indikator with position_x and position_y (as pixels)
    this.objektStrukturService.updateRoomIndikator(roomIndikatorId, {
      position_x: originalPixels.x,  // Save as pixels from original image
      position_y: originalPixels.y   // Save as pixels from original image
    }).subscribe({
      next: () => {
        console.log('Marker position saved successfully to room_indikator');
        // Update local indicator data
        if (marker.indicator) {
          marker.indicator.position_x = originalPixels.x;
          marker.indicator.position_y = originalPixels.y;
        }
        
        // Trigger auto-save of interactive plan
        this.autoSaveInteractivePlan();
      },
      error: (error) => {
        console.error('Error saving marker position:', error);
        this.snackBar.open('Fehler beim Speichern der Position', 'Schließen', { duration: 2000 });
      }
    });
  }

  // Check if save is possible
  canSaveInteractivePlan(): boolean {
    // Disable save in read-only mode
    if (this.readOnly) {
      return false;
    }
    return !!(
      this.planUploadId && 
      this.planOriginalFilename && 
      this.planFilePath &&
      this.stage &&
      this.planImage
    );
  }

  // Main save method
  public saveInteractivePlan(): void {
    // Prevent saving in read-only mode
    if (this.readOnly) {
      this.snackBar.open('Speichern ist im Ansichtsmodus nicht möglich', 'Schließen', { duration: 3000 });
      return;
    }
    
    console.log('saveInteractivePlan called', {
      planUploadId: this.planUploadId,
      planOriginalFilename: this.planOriginalFilename,
      planFilePath: this.planFilePath,
      canSave: this.canSaveInteractivePlan()
    });

    if (!this.canSaveInteractivePlan()) {
      this.snackBar.open('Fehler: Plan-Informationen fehlen', 'Schließen', { duration: 3000 });
      return;
    }

    // Show loading indicator
    const loadingSnackBar = this.snackBar.open('Speichere Plan...', '', { duration: 0 });

    try {
      // Export canvas as image (full content) - without emitting event to avoid download
      console.log('Exporting canvas for save...');
      const imageDataUrl = this.exportAsImageForSave('jpeg', true);
      
      if (!imageDataUrl) {
        loadingSnackBar.dismiss();
        this.snackBar.open('Fehler beim Exportieren des Plans', 'Schließen', { duration: 3000 });
        return;
      }

      console.log('Canvas exported, imageDataUrl length:', imageDataUrl.length);

      // Determine format from MIME type or default to jpeg
      const format = this.planMimeType?.includes('png') ? 'png' : 'jpeg';

      console.log('Saving to backend...', {
        originalUploadId: this.planUploadId,
        format: format,
        planId: this.planId
      });

      // Save to backend (only if not read-only)
      if (this.readOnly) {
        loadingSnackBar.dismiss();
        this.snackBar.open('Speichern ist im Ansichtsmodus nicht möglich', 'Schließen', { duration: 3000 });
        return;
      }
      this.planService.saveInteractivePlan(
        this.planUploadId!,
        imageDataUrl,
        format,
        this.planId
      ).subscribe({
        next: (response) => {
          console.log('Save response:', response);
          loadingSnackBar.dismiss();
          if (response.result === 1) {
            this.snackBar.open('Plan erfolgreich gespeichert', 'Schließen', { duration: 3000 });
            // Emit event to notify parent component
            this.planSaved.emit();
          } else {
            this.snackBar.open('Fehler beim Speichern', 'Schließen', { duration: 3000 });
          }
        },
        error: (error) => {
          loadingSnackBar.dismiss();
          console.error('Error saving interactive plan:', error);
          console.error('Error details:', {
            status: error.status,
            statusText: error.statusText,
            error: error.error,
            url: error.url
          });
          this.snackBar.open('Fehler beim Speichern: ' + (error.error?.error || error.message || 'Unbekannter Fehler'), 'Schließen', { duration: 4000 });
        }
      });
    } catch (error) {
      loadingSnackBar.dismiss();
      console.error('Error exporting plan:', error);
      this.snackBar.open('Fehler beim Exportieren', 'Schließen', { duration: 3000 });
    }
  }

  // Export image for saving (without emitting planExported event)
  private exportAsImageForSave(format: 'png' | 'jpeg' = 'png', exportFullContent: boolean = true): string {
    if (!this.stage) return '';
    
    if (exportFullContent && this.planImage) {
      // Export full content area regardless of zoom/pan
      return this.exportFullContentForSave(format);
    } else {
      // Export visible area only
      const dataUrl = this.stage.toDataURL({
        mimeType: `image/${format}`,
        quality: 0.9
      });
      // Don't emit planExported event - just return the data URL
      return dataUrl;
    }
  }

  private exportFullContentForSave(format: 'png' | 'jpeg'): string {
    if (!this.stage || !this.planImage || !this.backgroundLayer || !this.markersLayer) {
      return '';
    }

    // Store current state
    const currentScale = this.stage.scaleX();
    const currentPosition = this.stage.position();
    const currentZoomLevel = this.planState.zoomLevel;

    try {
      // Get full content bounds
      const fullBounds = this.calculateFullContentBounds();
      
      // Temporarily reset transform to base state (scale 1.0, position 0,0)
      this.stage.scale({ x: 1, y: 1 });
      this.stage.position({ x: 0, y: 0 });
      this.stage.batchDraw();

      // Export full area with higher quality
      const dataUrl = this.stage.toDataURL({
        mimeType: `image/${format}`,
        quality: 0.9,
        x: fullBounds.x,
        y: fullBounds.y,
        width: fullBounds.width,
        height: fullBounds.height,
        pixelRatio: 2 // Higher quality export
      });

      // Restore original transform
      this.stage.scale({ x: currentScale, y: currentScale });
      this.stage.position(currentPosition);
      this.stage.batchDraw();
      this.planState.zoomLevel = currentZoomLevel;

      // Don't emit planExported event - just return the data URL
      return dataUrl;
    } catch (error) {
      console.error('Error exporting full content:', error);
      // Restore state even on error
      this.stage.scale({ x: currentScale, y: currentScale });
      this.stage.position(currentPosition);
      this.stage.batchDraw();
      this.planState.zoomLevel = currentZoomLevel;
      return '';
    }
  }

  // Auto-save method (called on marker move)
  private autoSaveInteractivePlan(): void {
    // Only auto-save if we have the necessary information
    if (!this.canSaveInteractivePlan()) {
      return;
    }

    // Debounce auto-save to avoid too many requests
    // Use a timeout to batch multiple moves
    if (this.autoSaveTimeout) {
      clearTimeout(this.autoSaveTimeout);
    }

    // Skip auto-save in read-only mode
    if (this.readOnly) {
      return;
    }
    
    this.autoSaveTimeout = setTimeout(() => {
      // Export and save silently (no user notification for auto-save)
      try {
        const imageDataUrl = this.exportAsImageForSave('jpeg', true);
        if (imageDataUrl) {
          const format = this.planMimeType?.includes('png') ? 'png' : 'jpeg';
          this.planService.saveInteractivePlan(
            this.planUploadId!,
            imageDataUrl,
            format,
            this.planId
          ).subscribe({
            next: (response) => {
              console.log('Interactive plan auto-saved');
              if (response.result === 1) {
                // Emit event to notify parent component
                this.planSaved.emit();
              }
            },
            error: (error) => {
              console.error('Error auto-saving interactive plan:', error);
              // Don't show error to user for auto-save
            }
          });
        }
      } catch (error) {
        console.error('Error exporting plan for auto-save:', error);
      }
    }, 2000); // Wait 2 seconds after last move before saving
  }

  // Load existing positions from roomIndicators (which have position_x and position_y)
  private loadExistingPositions(): void {
    if (!this.planImage || !this.roomIndicators) {
      return;
    }
    
    // Clear existing markers
    this.planState.markers = [];
    if (this.markersLayer) {
      this.markersLayer.destroyChildren();
    }
    
    // Find indicators with saved positions
    const indicatorsWithPositions = this.roomIndicators.filter(ri => 
      ri.position_x !== null && ri.position_x !== undefined &&
      ri.position_y !== null && ri.position_y !== undefined
    );
    
    if (indicatorsWithPositions.length > 0) {
      // Load indicators with saved positions
      indicatorsWithPositions.forEach(indicator => {
        // Convert original image pixels to canvas coordinates
        const absolutePosition = this.convertOriginalPixelsToCanvasPosition(
          indicator.position_x!,  // This is pixels from original image
          indicator.position_y!    // This is pixels from original image
        );
        
        // Create marker
        const marker: PlanMarker = {
          id: `marker_${indicator.id}_${Date.now()}`,
          indicator: indicator,
          x: absolutePosition.x,
          y: absolutePosition.y,
          scale: 1,
          rotation: 0,
          visible: true,
          locked: false,
          shape: indicator.indikator?.symbol_id || '1'
        };
        
        this.planState.markers.push(marker);
        this.createMarkerShape(marker);
      });
    } else {
      // No existing positions - auto-place all active indicators
      this.autoPlaceAllIndicators();
    }
    
    if (this.markersLayer) {
      this.markersLayer.draw();
    }
  }

  /**
   * Automatically place all active room-indicators on the canvas in a grid layout
   */
  private autoPlaceAllIndicators(): void {
    if (!this.planImage || !this.roomIndicators || !this.markersLayer) {
      return;
    }
    
    // Get image bounds
    const imageX = this.planImage.x();
    const imageY = this.planImage.y();
    const imageWidth = this.planImage.width();
    const imageHeight = this.planImage.height();
    
    // Filter only active indicators that don't already have markers
    const activeIndicators = this.roomIndicators.filter(indicator => {
      const statusValue = indicator.status as any;
      const isActive = statusValue === 1 || statusValue === true;
      const alreadyPlaced = this.planState.markers.some(m => m.indicator.id === indicator.id);
      return isActive && !alreadyPlaced;
    });
    
    if (activeIndicators.length === 0) {
      return;
    }
    
    // Calculate grid layout
    const cols = Math.ceil(Math.sqrt(activeIndicators.length));
    const rows = Math.ceil(activeIndicators.length / cols);
    const spacingX = imageWidth / (cols + 1);
    const spacingY = imageHeight / (rows + 1);
    
    // Place each indicator in grid
    activeIndicators.forEach((indicator, index) => {
      const col = index % cols;
      const row = Math.floor(index / cols);
      
      // Position in grid (with some margin from edges)
      const x = imageX + spacingX * (col + 1);
      const y = imageY + spacingY * (row + 1);
      
      // Create marker
      const marker: PlanMarker = {
        id: `marker_${indicator.id}_${Date.now()}`,
        indicator: indicator,
        x: x,
        y: y,
        scale: 1,
        rotation: 0,
        visible: true,
        locked: false,
        shape: indicator.indikator?.symbol_id || '1'
      };
      
      this.planState.markers.push(marker);
      this.createMarkerShape(marker);
      
      // Auto-save position (convert to original image pixels)
      const originalPixels = this.convertCanvasPositionToOriginalPixels(x, y);
      setTimeout(() => {
        this.saveMarkerPosition(marker, { x: originalPixels.x, y: originalPixels.y });
      }, 100);
    });
    
    if (this.markersLayer) {
      this.markersLayer.draw();
    }
  }
}
